import { Warehouse } from "@nuorder/client-cart-api/dist/types/modules/brand/models";
import { Schema, SchemaField } from "types/schema";
import { CellValueChangedEvent, ICellRendererParams, GridApi } from "ag-grid-community";
import type { AllocationType } from "types/cart";
import { IGridEntry, IDateRange, IAvailability, CumulativeDate, LineItemAttributes } from "types/cart/shipment-review";
import type { IOrderCurrency, ISizeHeaderPrices } from "types/cart/shipment-review";
import { IViewDefinition } from "types/view-definitions";
import type { IProductSize } from "types/product";

/**
 * Provides explicit typing of ag-grid callback params, where `data` and the grid `context` are of `any` type.
 */
export type GridShipmentRowCallbackParams<T extends { data: any } = { data: any }> = CastProp<T, "data", IRowData>;
export type ShipmentCellValueChangeEvent = CastProp<
    GridShipmentRowCallbackParams<CellValueChangedEvent>,
    "oldValue" | "newValue",
    SizeData
>;
export type ShipmentSizeRendererParams = GridShipmentRowCallbackParams<ICellRendererParams>;

export interface DetailGridContext {
    parentApi: GridApi | undefined;
    suppressPrice: boolean;
    hasSingleDelivery: boolean;
}

export enum ColumnTypes {
    Size = "size",
    FdmAttribute = "FdmAttribute",
}

export interface SizeData {
    /**
     * @deprecated use detailRow.lineItemId
     * TODO: remove
     */
    id?: string;
    quantity: number;
    /** `Number` for ATS inventory, `null` for prebook */
    available: number | null;
    /** `Number` for ATS inventory, `null` for prebook */
    displayQuantity: string | null;
    unitsPerPack: number;
    size: IProductSize;
    allocated: AllocationType | null;
    isOverordered: boolean;
    isVirtual?: boolean; // Build Order: size for non-existing line item
    pricing: {
        wholesalePrice: number;
        retailPrice: number;
        originalWholesalePrice?: number;
        discountedPrice?: number;
    };
}

export interface IShipment {
    shipStart: Date | null;
    shipEnd: Date | null;
}

export interface IRowData {
    /** @deprecated assign and use {@link IRowData} properties instead */
    item: IGridEntry;
    currency: IOrderCurrency;
    id: string;
    lineItemId: string;
    productId: string;
    splitFields: IShipment;
    prebook: boolean;
    canChangePrebook: boolean;
    available: boolean;
    enforceAvailableUntil?: boolean;
    warehouse: Warehouse | null;
    availableWarehouses: Warehouse[];
    availability?: IAvailability;
    dbpPrices?: ISizeHeaderPrices;
    lineItemAttributes: LineItemAttributes;
    sizes: Record<string, SizeData>;
    totals: IDetailsTotal;
    shipmentKey: string;
    allocated: AllocationType | null;
    hideInventory: boolean;
    holdInventory: boolean;
    isVirtual?: boolean; // indicated that line item does not exist in the WO model
}

export interface Inventory {
    available: number | null;
    displayQuantity: string;
}

export interface IItem {
    compositeKeyFields: {
        warehouse: string;
        [fieldName: string]: any;
    };
    currency: IOrderCurrency;
    defaultDeliveryWindow: number;
    enforceAvailableUntil: boolean;
    item: IGridEntry;
    itemView: IViewDefinition;
    minimumDeliveryWindow: number;
    cumulativeDate: CumulativeDate;
    pricing: object;
    productSchema: Schema;
    shipEnd: Date;
    shipStart: Date;
    orderDiscount?: number | null;
}

export interface IProductItem {
    colId?: string;
    items: IItem[];
    uniqueDates: IDateRange[];
}

export interface ShipmentReviewModalData {
    items: IItem[];
    uniqueDates: IDateRange[];
    currencyCode: string;
}

export interface IPricing {
    quantity?: number;
    totalWholesalePrice?: number;
    totalOriginalWholesalePrice?: number;
    currency?: IOrderCurrency;
    totalRetailPrice?: number;
}

export interface ITotal extends IPricing {
    total: IPricing;
}

export interface IDetailsTotal extends IPricing {
    headerName?: string;
    sizes: {
        [size: string]: IPricing; // totals for each size
    };
}

export interface ISize {
    id: string;
    name: string;
}

export interface IGridContext {
    itemView: IViewDefinition;
    brandId: string;
    fdm: SchemaField[];
    isBuildOrderEnabled?: boolean;
}
